import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./PromoLinkPage.css";

// 관리자 > 프로모션 링크. 학부모 반응 링크(쓰레드·카페 자료 받기 + 무료 레벨테스트)를 만든다.
// 타겟 학교 / 학년 / 교과서(목록에서 선택) 를 고르면 링크가 나오고, 같은 줄에서 자료 파일을
// 올리고 댓글 단 아이디를 넣는다. 서버: api/echo/promo_link_views.py

const CHANNEL_LIST = [
  { value: "threads", label: "쓰레드" },
  { value: "cafe", label: "카페" },
];

const LinkRow = ({ link, onChanged }) => {
  const fileRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [ids, setIds] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState("");

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy("file");
    setResult("");
    const formData = new FormData();
    formData.append("key", link.key);
    formData.append("file", file);
    try {
      await apiClient.post("/api/v1/echo/admin/promo-links/file", formData, {
        withAuth: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult("자료 파일을 올렸어요.");
      onChanged();
    } catch (caught) {
      setResult(caught.response?.data?.error || "파일을 올리지 못했어요.");
    } finally {
      setBusy("");
    }
  };

  const addIds = async () => {
    setBusy("ids");
    setResult("");
    try {
      const response = await apiClient.post(
        "/api/v1/echo/admin/promo-links/entries",
        { key: link.key, ids },
        { withAuth: true }
      );
      const { added, existing, invalid } = response.data;
      setResult(
        `새로 넣음 ${added.length}명 · 이미 있음 ${existing.length}명` +
          (invalid.length ? ` · 모양이 틀림 ${invalid.join(", ")}` : "")
      );
      setIds("");
      onChanged();
    } catch (caught) {
      setResult(caught.response?.data?.error || "명단에 넣지 못했어요.");
    } finally {
      setBusy("");
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(link.url);
    setResult("링크를 복사했어요.");
  };

  return (
    <div className="promo-link__row">
      <div className="promo-link__row-main">
        <div className="promo-link__row-title">
          <span className={`promo-link__chip promo-link__chip--${link.channel}`}>
            {link.channel === "cafe" ? "카페" : "쓰레드"}
          </span>
          {link.title}
        </div>
        <div className="promo-link__row-meta">
          {[link.school_name, link.grade, link.textbook_label].filter(Boolean).join(" · ")}
          {" · "}
          {link.file_ready ? "자료 있음" : "자료 아직 없음"} · 명단 {link.entry_count}명 · 받음{" "}
          {link.download_count}회
        </div>
        <div className="promo-link__url">{link.url}</div>
      </div>
      <div className="promo-link__row-actions">
        <button type="button" onClick={copyLink}>링크 복사</button>
        <button type="button" disabled={busy === "file"} onClick={() => fileRef.current.click()}>
          {busy === "file" ? "올리는 중..." : link.file_ready ? "자료 바꾸기" : "자료 올리기"}
        </button>
        <button type="button" onClick={() => setIsOpen((prev) => !prev)}>
          {link.handle_label} 넣기
        </button>
        <input ref={fileRef} type="file" accept=".zip,.pdf" hidden onChange={(event) => uploadFile(event.target.files[0])} />
      </div>
      {isOpen && (
        <div className="promo-link__ids">
          <textarea
            rows={4}
            value={ids}
            placeholder={
              link.channel === "cafe"
                ? "카페 닉네임을 한 줄에 하나씩 (띄어쓰기가 있어도 됩니다)"
                : "쓰레드 아이디를 줄바꿈, 띄어쓰기, 쉼표로 나눠서"
            }
            onChange={(event) => setIds(event.target.value)}
          />
          <button type="button" disabled={busy === "ids" || !ids.trim()} onClick={addIds}>
            {busy === "ids" ? "넣는 중..." : "명단에 넣기"}
          </button>
        </div>
      )}
      {result && <div className="promo-link__result">{result}</div>}
    </div>
  );
};

const PromoLinkPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [grade, setGrade] = useState("");
  const [textbookCode, setTextbookCode] = useState("");
  const [channel, setChannel] = useState("threads");
  const [isCreating, setIsCreating] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(() => {
    apiClient
      .get("/api/v1/echo/admin/promo-links", { withAuth: true })
      .then((response) => setData(response.data))
      .catch((caught) => setError(caught.response?.data?.error || "불러오지 못했습니다."));
  }, []);

  useEffect(load, [load]);

  const textbookOptionList = useMemo(
    () => (data ? data.textbook_list.filter((book) => book.grade === grade) : []),
    [data, grade]
  );

  const create = async () => {
    setIsCreating(true);
    setMessage("");
    try {
      const response = await apiClient.post(
        "/api/v1/echo/admin/promo-links",
        { school_name: schoolName, grade, textbook_code: textbookCode, channel },
        { withAuth: true }
      );
      setMessage(`만들었어요: ${response.data.link.url}`);
      load();
    } catch (caught) {
      setMessage(caught.response?.data?.error || "만들지 못했어요.");
    } finally {
      setIsCreating(false);
    }
  };

  if (error) return <p className="promo-link">{error}</p>;
  if (!data) return null;

  return (
    <div className="promo-link">
      <div className="section-title">프로모션 링크</div>
      <p className="promo-link__lead">
        학부모에게 자료를 나눠 주고 무료 레벨테스트로 이어 주는 링크를 만듭니다. 링크를 만든 뒤 자료
        파일을 올리고, 댓글 단 아이디를 넣으면 그 사람만 받을 수 있어요.
      </p>

      <section className="promo-link__card">
        <div className="promo-link__form">
          <label>
            <span>타겟 학교</span>
            <input value={schoolName} placeholder="예: 어은중 (비우면 학교 없이)" onChange={(event) => setSchoolName(event.target.value)} />
          </label>
          <label>
            <span>학년</span>
            <select
              value={grade}
              onChange={(event) => {
                setGrade(event.target.value);
                setTextbookCode("");
              }}
            >
              <option value="">학년 선택</option>
              {data.grade_list.map((one) => (
                <option key={one} value={one}>{one}</option>
              ))}
            </select>
          </label>
          <label>
            <span>교과서</span>
            <select value={textbookCode} disabled={!grade} onChange={(event) => setTextbookCode(event.target.value)}>
              <option value="">{grade ? "교과서 선택" : "학년을 먼저 고르세요"}</option>
              {textbookOptionList.map((book) => (
                <option key={book.code} value={book.code}>
                  {book.label} ({book.subject} · {book.curriculum} 개정)
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>올릴 곳</span>
            <select value={channel} onChange={(event) => setChannel(event.target.value)}>
              {CHANNEL_LIST.map((one) => (
                <option key={one.value} value={one.value}>{one.label}</option>
              ))}
            </select>
          </label>
          <button type="button" className="promo-link__create" disabled={isCreating || !textbookCode} onClick={create}>
            {isCreating ? "만드는 중..." : "링크 만들기"}
          </button>
        </div>
        {message && <div className="promo-link__result">{message}</div>}
      </section>

      <section className="promo-link__card">
        <div className="promo-link__card-title">만든 링크 {data.link_list.length}개</div>
        {data.link_list.map((link) => (
          <LinkRow key={link.key} link={link} onChanged={load} />
        ))}
      </section>
    </div>
  );
};

export default PromoLinkPage;
